import { noop } from "lodash";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { AccountEntityType } from "@/lib/constants/account-types";
import type { TranslationKey } from "@/localisation/types";
import { useGetLocalAccount } from "@/queries/ledger/accounts/get-local-account";

import { getLedgerStatusStyle } from "../status-styles";

import type { AccountDetails } from "@/apis/gql/types";
import { BankAccountDetails } from "./bank-account";
import { ExpenseAccountDetails } from "./expense-account";

const CompMap = {
  [AccountEntityType.EXPENSE_ACCOUNT]: ExpenseAccountDetails,
  [AccountEntityType.BANK_ACCOUNT]: BankAccountDetails,
} as const;

type Props = {
  focusedRowId: string | undefined;
  onClose: () => void;
};

export const ChartOfAccountsDrawer = ({ focusedRowId, onClose }: Props) => {
  const { t } = useTranslation();

  const handleDrawerClose = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const { data } = useGetLocalAccount({ accountId: focusedRowId });

  const { entityType, name, number } = data;

  const renderComp = () => {
    if (entityType === AccountEntityType.BANK_ACCOUNT) {
      return <BankAccountDetails account={data} />;
    }

    if (entityType === AccountEntityType.EXPENSE_ACCOUNT) {
      return <ExpenseAccountDetails account={data.entity as AccountDetails} />;
    }

    return null;
  };

  return (
    <Sheet open={!!focusedRowId} onOpenChange={handleDrawerClose}>
      <SheetContent
        className="min-w-[30%]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <div>
            <Badge className={getLedgerStatusStyle(entityType)}>
              {
                t(
                  `journal_accounts.entity_type.${entityType}` as TranslationKey,
                ) as string
              }
            </Badge>
          </div>
          <SheetTitle>{`${t("account")}: ${name} (${number})`}</SheetTitle>
        </SheetHeader>
        {renderComp()}
      </SheetContent>
    </Sheet>
  );
};
